import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Save, Printer, AlertCircle, Stethoscope, Activity, User, Phone, Heart } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { exportFormPdfFromDom } from '../../utils/domFormPdf';
import { fillEmpty, mergeSaved } from '../../utils/formMerge';
import SignatureCanvas from 'react-signature-canvas';

const PersonalCareHomeAssessment = () => {
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const signatureRef = useRef(null);
  
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Assessment Type
    assessmentType: 'initial',
    assessmentDate: new Date().toISOString().split('T')[0],
    admissionDate: '',
    
    // Client Info
    clientName: '',
    dateOfBirth: '',
    
    // Supports
    formalSupports: '',
    familyFriendSupports: '',
    familyFriendName: '',
    familyFriendAddress: '',
    familyFriendPhone: '',
    
    // Healthcare Providers
    primaryPhysician: '',
    physicianPhone: '',
    dentist: '',
    dentistPhone: '',
    caseManager: '',
    caseManagerPhone: '',
    
    // Medical Information
    primaryDiagnosis: '',
    hasDementia: false,
    hasMentalIllness: false,
    historyOfFalls: false,
    safetyIssues: '',
    
    // Medical Needs (checkboxes)
    medicalNeeds: {
      feedingTube: false,
      oxygenTherapy: false,
      catheter: false,
      colostomy: false,
      woundCare: false,
      diabetesManagement: false,
      ivTherapy: false,
      dialysis: false,
      ventilator: false,
      tracheostomy: false,
    },
    
    // Referral
    referralDate: '',
    referralSource: '',
    
    // Activities of Daily Living (ADLs)
    adl: {
      bathing: '',
      dressing: '',
      toileting: '',
      transferring: '',
      eating: '',
      continence: '',
    },
    
    // Instrumental Activities of Daily Living (IADLs)
    iadl: {
      mealPreparation: '',
      housekeeping: '',
      laundry: '',
      shopping: '',
      transportation: '',
      medicationManagement: '',
      financialManagement: '',
      phoneUse: '',
    },
    
    // Communication
    hearingAbility: '',
    visionAbility: '',
    understandingAbility: '',
    memoryAbility: '',
    
    // Medications
    canSelfAdministerMeds: '',
    medicationNotes: '',
    
    // Mobility
    mobilityAids: [],
    mobilityNotes: '',
    
    // Additional Notes
    additionalNotes: '',
    
    // Signature
    assessorName: '',
    assessorTitle: '',
    signatureDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (patientId) {
      fetchPatientData();
      api.get(`/admission-forms/${patientId}/home_assessment`).then(r => { const d = r.data?.form?.data; if (d) setFormData(prev => mergeSaved(prev, d)); }).catch(() => {});
    }
  }, [patientId]);

  const fetchPatientData = async () => {
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data) {
        const patient = response.data.patient || response.data;
        setFormData(prev => fillEmpty(prev, {
          
          clientName: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
          dateOfBirth: patient.dateOfBirth || '',
          primaryPhysician: patient.physicianName || patient.primaryPhysician || '',
          physicianPhone: patient.physicianPhone || patient.primaryPhysicianPhone || '',
          primaryDiagnosis: patient.primaryDiagnosis || '',
          familyFriendName: patient.emergencyContactName || '',
          familyFriendPhone: patient.emergencyContactPhone || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('medicalNeeds_')) {
      const key = name.replace('medicalNeeds_', '');
      setFormData(prev => ({
        ...prev,
        medicalNeeds: { ...prev.medicalNeeds, [key]: checked }
      }));
    } else if (name.startsWith('adl_')) {
      const key = name.replace('adl_', '');
      setFormData(prev => ({
        ...prev,
        adl: { ...prev.adl, [key]: value }
      }));
    } else if (name.startsWith('iadl_')) {
      const key = name.replace('iadl_', '');
      setFormData(prev => ({
        ...prev,
        iadl: { ...prev.iadl, [key]: value }
      }));
    } else if (name === 'mobilityAids') {
      const aids = formData.mobilityAids.includes(value)
        ? formData.mobilityAids.filter(a => a !== value)
        : [...formData.mobilityAids, value];
      setFormData(prev => ({ ...prev, mobilityAids: aids }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const signatureData = signatureRef.current && !signatureRef.current.isEmpty()
        ? signatureRef.current.toDataURL() : '';

      const payload = {
        ...formData,
        assessorSignature: signatureData,
        patientId,
        formType: 'home_assessment',
      };

      await api.post('/admission-forms/save', payload);
      toast.success('Personal Care Home Assessment saved successfully');
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const adlOptions = ['Independent', 'Supervision', 'Limited Assistance', 'Extensive Assistance', 'Total Dependence'];
  const iadlOptions = ['Independent', 'Some Help Needed', 'Unable to Perform'];
  const communicationOptions = ['Good', 'Fair', 'Poor', 'Unable'];

  if (!patientId) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <p className="text-yellow-800">Please select a patient to complete this form.</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="home-assessment-form">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-t-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Stethoscope className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Personal Care Home Assessment</h1>
                <p className="text-pink-100">IVORY HOME HEALTH CARE, INC.</p>
              </div>
            </div>
            <div className="flex gap-2 print:hidden">
              <button
                data-testid="print-form-btn"
                onClick={() => exportFormPdfFromDom(document.getElementById('main-content'), 'Personal Care Home Assessment', patientId)}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-white text-pink-600 hover:bg-pink-50 rounded-lg transition-colors font-medium"
                data-testid="save-form-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Form'}
              </button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-b-xl shadow-lg p-8 space-y-8">
          {/* Assessment Type */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Assessment Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Type of Assessment</label>
                <div className="flex flex-wrap gap-4">
                  {['initial', 'admission', 'annual', 'significant_change'].map(type => (
                    <label key={type} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="assessmentType"
                        value={type}
                        checked={formData.assessmentType === type}
                        onChange={handleChange}
                        className="w-4 h-4 text-pink-500 focus:ring-pink-500"
                      />
                      <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Date</label>
                <input
                  type="date"
                  name="assessmentDate"
                  value={formData.assessmentDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admission Date</label>
                <input
                  type="date"
                  name="admissionDate"
                  value={formData.admissionDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>
          </section>

          {/* Client Info */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-pink-500" />
              Client Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>
          </section>

          {/* Support System */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-pink-500" />
              Support System
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Formal Supports (agencies, services)</label>
                <textarea
                  name="formalSupports"
                  value={formData.formalSupports}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Family/Friend Supports</label>
                <textarea
                  name="familyFriendSupports"
                  value={formData.familyFriendSupports}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Family/Friend Name</label>
                  <input
                    type="text"
                    name="familyFriendName"
                    value={formData.familyFriendName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="familyFriendAddress"
                    value={formData.familyFriendAddress}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="familyFriendPhone"
                    value={formData.familyFriendPhone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Healthcare Providers */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              Healthcare Providers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Physician</label>
                <input
                  type="text"
                  name="primaryPhysician"
                  value={formData.primaryPhysician}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
                <input
                  type="tel"
                  name="physicianPhone"
                  value={formData.physicianPhone}
                  onChange={handleChange}
                  placeholder="Phone"
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dentist</label>
                <input
                  type="text"
                  name="dentist"
                  value={formData.dentist}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
                <input
                  type="tel"
                  name="dentistPhone"
                  value={formData.dentistPhone}
                  onChange={handleChange}
                  placeholder="Phone"
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Manager</label>
                <input
                  type="text"
                  name="caseManager"
                  value={formData.caseManager}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
                <input
                  type="tel"
                  name="caseManagerPhone"
                  value={formData.caseManagerPhone}
                  onChange={handleChange}
                  placeholder="Phone"
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>
          </section>

          {/* Medical Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-pink-500" />
              Medical Information
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referral Source</label>
                  <input type="text" name="referralSource" value={formData.referralSource} onChange={handleChange} data-testid="home-referral-source"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referral Date</label>
                  <input type="date" name="referralDate" value={formData.referralDate} onChange={handleChange} data-testid="home-referral-date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Diagnosis</label>
                <input
                  type="text"
                  name="primaryDiagnosis"
                  value={formData.primaryDiagnosis}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="hasDementia"
                    checked={formData.hasDementia}
                    onChange={handleChange}
                    className="w-4 h-4 rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm">Dementia</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="hasMentalIllness"
                    checked={formData.hasMentalIllness}
                    onChange={handleChange}
                    className="w-4 h-4 rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm">Mental Illness</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="historyOfFalls"
                    checked={formData.historyOfFalls}
                    onChange={handleChange}
                    className="w-4 h-4 rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm">History of Falls</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Safety Issues</label>
                <textarea
                  name="safetyIssues"
                  value={formData.safetyIssues}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                />
              </div>
            </div>
          </section>

          {/* Medical Needs */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Medical Needs</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { key: 'feedingTube', label: 'Feeding Tube' },
                { key: 'oxygenTherapy', label: 'Oxygen Therapy' },
                { key: 'catheter', label: 'Catheter' },
                { key: 'colostomy', label: 'Colostomy' },
                { key: 'woundCare', label: 'Wound Care' },
                { key: 'diabetesManagement', label: 'Diabetes Mgmt' },
                { key: 'ivTherapy', label: 'IV Therapy' },
                { key: 'dialysis', label: 'Dialysis' },
                { key: 'ventilator', label: 'Ventilator' },
                { key: 'tracheostomy', label: 'Tracheostomy' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100">
                  <input
                    type="checkbox"
                    name={`medicalNeeds_${key}`}
                    checked={formData.medicalNeeds[key]}
                    onChange={handleChange}
                    className="w-4 h-4 rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Activities of Daily Living */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activities of Daily Living (ADLs)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'bathing', label: 'Bathing' },
                { key: 'dressing', label: 'Dressing' },
                { key: 'toileting', label: 'Toileting' },
                { key: 'transferring', label: 'Transferring' },
                { key: 'eating', label: 'Eating' },
                { key: 'continence', label: 'Continence' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <select
                    name={`adl_${key}`}
                    value={formData.adl[key]}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">Select...</option>
                    {adlOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </section>

          {/* IADLs */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Instrumental Activities of Daily Living (IADLs)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'mealPreparation', label: 'Meal Preparation' },
                { key: 'housekeeping', label: 'Housekeeping' },
                { key: 'laundry', label: 'Laundry' },
                { key: 'shopping', label: 'Shopping' },
                { key: 'transportation', label: 'Transportation' },
                { key: 'medicationManagement', label: 'Medication Management' },
                { key: 'financialManagement', label: 'Financial Management' },
                { key: 'phoneUse', label: 'Phone Use' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <select
                    name={`iadl_${key}`}
                    value={formData.iadl[key]}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">Select...</option>
                    {iadlOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </section>

          {/* Communication */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Communication Abilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { key: 'hearingAbility', label: 'Hearing' },
                { key: 'visionAbility', label: 'Vision' },
                { key: 'understandingAbility', label: 'Understanding' },
                { key: 'memoryAbility', label: 'Memory' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <select
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="">Select...</option>
                    {communicationOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </section>

          {/* Mobility Aids */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Mobility Aids</h2>
            <div className="flex flex-wrap gap-4 mb-4">
              {['None', 'Cane', 'Walker', 'Wheelchair', 'Hospital Bed', 'Lift Chair', 'Other'].map(aid => (
                <label key={aid} className="flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100">
                  <input
                    type="checkbox"
                    value={aid}
                    checked={formData.mobilityAids.includes(aid)}
                    onChange={(e) => handleChange({ target: { name: 'mobilityAids', value: aid } })}
                    className="w-4 h-4 rounded text-pink-500 focus:ring-pink-500"
                  />
                  <span className="text-sm">{aid}</span>
                </label>
              ))}
            </div>
            <textarea
              name="mobilityNotes"
              value={formData.mobilityNotes}
              onChange={handleChange}
              placeholder="Additional mobility notes..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
            />
          </section>

          {/* Additional Notes */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h2>
            <textarea
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
            />
          </section>

          {/* Signature */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Assessor Signature</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Signature</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'w-full h-24 bg-white',
                      style: { width: '100%', height: '96px' }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => signatureRef.current?.clear()}
                  className="mt-1 text-xs text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assessor Name</label>
                  <input
                    type="text"
                    name="assessorName"
                    value={formData.assessorName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    name="assessorTitle"
                    value={formData.assessorTitle}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    name="signatureDate"
                    value={formData.signatureDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PersonalCareHomeAssessment;
